import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { RiskBadge, StageBadge } from '@/components/common/Bits';
import { RISK_HSL, fmtDate } from '@/lib/constants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Search01Icon,
  Task01Icon,
  SecurityCheckIcon,
  BalanceScaleIcon,
  Activity01Icon,
  ArrowRight01Icon,
  AlertCircleIcon,
  Calendar03Icon,
  Radar01Icon,
  SparklesIcon,
  ChartHistogramIcon,
  AiInnovation01Icon,
  Rocket01Icon,
  Shield01Icon,
  UserMultipleIcon,
  Settings02Icon,
  InboxIcon,
  Alert01Icon,
  Road01Icon,
} from '@hugeicons/core-free-icons';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  AreaChart,
  Area,
  CartesianGrid,
  ScatterChart,
  Scatter,
  ZAxis,
  Legend,
} from 'recharts';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { cn } from '@/lib/utils';

// Las 7 etapas del ciclo de gobierno. Cada etapa mide una fuente distinta
// (Shadow AI, iniciativas por etapa, decisiones del Comité, reevaluaciones),
// por eso los conteos NO suman el total de iniciativas registradas.
const CYCLE_STEPS = [
  { key: 'Detectar', icon: Search01Icon, desc: 'Shadow AI activa', source: 'shadow' },
  { key: 'Registrar', icon: Task01Icon, desc: 'Idea / exploración', source: 'initiative' },
  { key: 'Evaluar', icon: SecurityCheckIcon, desc: 'En evaluación', source: 'initiative' },
  { key: 'Decidir', icon: BalanceScaleIcon, desc: 'En Comité', source: 'committee' },
  { key: 'Construir', icon: Settings02Icon, desc: 'POC / piloto / impl.', source: 'initiative' },
  { key: 'Monitorear', icon: Activity01Icon, desc: 'Producción', source: 'initiative' },
  { key: 'Reevaluar', icon: ArrowRight01Icon, desc: 'Reevaluaciones', source: 'reeval' },
];

// Destino de drill-down para cada etapa del ciclo. Las etapas basadas en
// iniciativas llevan al registro pre-filtrado por etapa (?stage=...).
// Los destinos usan los MISMOS grupos de etapas que los conteos del backend,
// para que el número de la etapa coincida con los resultados filtrados.
// Backend cycle: Registrar=Idea+Exploración; Evaluar=Prefactibilidad+Evaluación;
// Construir=POC+Piloto+Implementación; Monitorear=Producción+Escalamiento.
const CYCLE_LINKS = {
  Detectar: '/shadow-ai',
  Registrar: '/registro?stage=Idea,Exploración',
  Evaluar: '/registro?stage=Prefactibilidad,Evaluación',
  Decidir: '/comite',
  Construir: '/registro?stage=POC,Piloto,Implementación',
  Monitorear: '/registro?stage=Producción,Escalamiento',
  Reevaluar: '/reevaluaciones',
};

const tooltipStyle = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 10,
  fontSize: 12,
  boxShadow: 'var(--shadow-float)',
};

// Mapea el eje de valor declarado en la iniciativa (value_axis) al pilar visual.
const AXIS_TO_PILLAR = {
  Personas: 'Valor para las Personas',
  Operación: 'Valor para la Operación',
  Productividad: 'Productividad y Sostenibilidad',
};

// Formatea un monto CLP a "MM CLP" (millones). Devuelve '—' si no hay dato.
function fmtMMCLP(clp) {
  if (clp == null || clp === 0) return '—';
  const mm = clp / 1_000_000;
  const value = mm >= 100 ? Math.round(mm) : Math.round(mm * 10) / 10;
  return `${value.toLocaleString('es-CL')} MM`;
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get('/dashboard')
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="space-y-6">
          <Skeleton className="h-52 rounded-2xl" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  const t = data.totals;
  const riskData = Object.entries(data.by_risk).map(([name, value]) => ({ name, value }));
  const providerData = Object.entries(data.by_provider)
    .map(([name, value]) => ({ name: name.replace(' (Microsoft)', ''), value }))
    .sort((a, b) => b.value - a.value);

  // Ejes de valor VIA (1-3) calculados en el backend desde las iniciativas.
  const byAxis = data.value_axes?.by_axis ?? {};
  // Devuelve el indicador de un pilar: valor anual en MM CLP o, si aún no hay
  // monto cargado, el número de iniciativas que declaran ese eje.
  const axisIndicator = (axisKey) => {
    const a = byAxis[axisKey];
    if (!a) return '—';
    if (a.annual_value_clp > 0) return fmtMMCLP(a.annual_value_clp);
    if (a.count > 0) return `${a.count} inic.`;
    return '—';
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 space-y-8">
      {/* ── HEADER — Clean title with exposure badge ── */}
      <MotionSection>
        <div>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1
                id="dashboard-title"
                className="font-display text-2xl md:text-3xl font-extrabold text-foreground leading-tight">
                Panel principal
              </h1>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                Visión ejecutiva y operacional del portafolio institucional de inteligencia artificial.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className="bg-primary/8 text-primary border-primary/20 py-1.5 px-3 text-xs">
                <HugeiconsIcon icon={Shield01Icon} size={14} className="mr-1.5" aria-hidden="true" />
                Exposición global: {t.exposure}/100
              </Badge>
              <DemoDataBadge />
            </div>
          </div>
        </div>
      </MotionSection>

      {/* ── METRICS — 4+4 stat cards, clear labels for executives ── */}
      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.04}>
        <StaggerItem>
          <StatCard
            icon={AiInnovation01Icon}
            label="Iniciativas registradas"
            value={t.initiatives}
            subtitle="Ver portafolio"
            to="/registro"
            tooltip="Total de casos de uso de IA registrados en VIA, independiente de su estado. Incluye ideas, pilotos y sistemas en producción."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={Rocket01Icon}
            label="En producción"
            value={t.production}
            subtitle="Ver en producción"
            to="/registro?stage=Producción,Escalamiento"
            tooltip="Iniciativas que completaron su evaluación de riesgo, fueron aprobadas por el Comité y están operando con monitoreo activo."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={ChartHistogramIcon}
            label="Pilotos activos"
            value={t.pilots}
            subtitle="Ver pilotos"
            to="/registro?stage=Piloto,POC"
            tooltip="Iniciativas en fase piloto: aprobadas condicionalmente, operando en entorno controlado para medir valor antes de pasar a producción."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={SecurityCheckIcon}
            label="Evaluaciones abiertas"
            value={t.open_evaluations ?? t.open_assessments ?? 0}
            subtitle="Ver evaluaciones"
            to="/registro?evaluacion=abierta"
            tooltip="Evaluaciones de riesgo asignadas a evaluadores que aún no han sido completadas. Cada iniciativa pasa por este proceso antes de ir al Comité."
          />
        </StaggerItem>
      </StaggerContainer>

      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.04}>
        <StaggerItem>
          <StatCard
            icon={Radar01Icon}
            label="Shadow AI detectada"
            value={t.shadow_detected}
            subtitle="Ver Shadow AI"
            variant="alert"
            to="/shadow-ai"
            tooltip="Herramientas de IA identificadas en uso dentro de la organización que no han sido registradas ni evaluadas formalmente en VIA."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={BalanceScaleIcon}
            label="Decisiones pendientes"
            value={t.pending_decisions}
            subtitle="Ver Comité"
            variant="alert"
            to="/comite"
            tooltip="Iniciativas que completaron su evaluación de riesgo y están en la mesa del Comité de IA esperando una resolución (aprobar, condicionar o rechazar)."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={Activity01Icon}
            label="Reevaluaciones activas"
            value={t.active_reevaluations}
            subtitle="Ver reevaluaciones"
            variant="alert"
            to="/reevaluaciones"
            tooltip="Iniciativas en producción que tuvieron cambios significativos (proveedor, datos, alcance) y requieren una nueva evaluación de riesgo."
          />
        </StaggerItem>
        <StaggerItem>
          <ExposureCard value={t.exposure} />
        </StaggerItem>
      </StaggerContainer>

      {/* ── DEMAND & PORTFOLIO HEALTH — new metrics ── */}
      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.04}>
        <StaggerItem>
          <StatCard
            icon={InboxIcon}
            label="Intakes pendientes"
            value={data.intakes_pendientes ?? 0}
            subtitle="Ver demanda"
            to="/demanda"
            tooltip="Señales de demanda recibidas que aún no se han convertido en iniciativas formales ni descartado."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={Alert01Icon}
            label="Alertas proveedor"
            value={data.alertas_proveedor_activas ?? 0}
            subtitle="Ver alertas"
            variant="alert"
            to="/alertas-proveedor"
            tooltip="Alertas activas de contacto prematuro con proveedores de IA sin pasar por el proceso de gobernanza formal."
          />
        </StaggerItem>
        <StaggerItem>
          <StatCard
            icon={UserMultipleIcon}
            label="Contacto vencido"
            value={data.relaciones_contacto_vencido ?? 0}
            subtitle="Ver relaciones"
            variant="alert"
            to="/relaciones-negocio"
            tooltip="Relaciones con el negocio que no han tenido actualización en más de 14 días, lo que puede indicar pérdida de engagement."
          />
        </StaggerItem>
        <StaggerItem>
          <PortfolioHealthCard health={data.salud_portafolio} />
        </StaggerItem>
      </StaggerContainer>

      {/* ── HORIZON DISTRIBUTION — mini bar chart ── */}
      {data.distribucion_horizonte && (
        <MotionSection delay={0.08}>
          <Card className="border-border/50 rounded-2xl">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <HugeiconsIcon icon={Road01Icon} size={14} className="text-primary-glow" aria-hidden="true" />
                </div>
                <CardTitle className="font-display text-sm font-bold">Distribución por horizonte temporal</CardTitle>
                <TooltipProvider delayDuration={200}>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                        aria-label="Información sobre distribución por horizonte">
                        <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                      Clasifica las iniciativas por su horizonte de ejecución. H1: entrega en 0-30 días. H2: 31-90 días.
                      H3: 3-6 meses. H4: backlog sin fecha comprometida. Permite visualizar la carga de trabajo
                      inmediata vs. futura.
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: 'H1 (0-30d)', value: data.distribucion_horizonte?.H1 ?? 0 },
                    { name: 'H2 (31-90d)', value: data.distribucion_horizonte?.H2 ?? 0 },
                    { name: 'H3 (3-6m)', value: data.distribucion_horizonte?.H3 ?? 0 },
                    { name: 'H4 (Backlog)', value: data.distribucion_horizonte?.H4 ?? 0 },
                  ]}
                  margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar
                    dataKey="value"
                    name="Iniciativas"
                    fill="hsl(var(--primary))"
                    radius={[6, 6, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </MotionSection>
      )}

      {/* ── VIA AXES — clear value indicators ── */}
      <MotionSection delay={0.1}>
        <div>
          <div className="flex items-center gap-2 mb-5">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <HugeiconsIcon icon={Shield01Icon} size={14} className="text-primary-glow" aria-hidden="true" />
            </div>
            <h2 className="font-display text-lg font-extrabold">Ejes de valor VIA</h2>
          </div>

          <div className="rounded-2xl border border-border/50 bg-card p-6 space-y-5">
            {/* Subtitle */}
            <p className="text-xs text-muted-foreground max-w-2xl">
              Toda iniciativa de IA se evalúa contra estos 4 ejes. Los ejes 1–3 son el valor buscado; el eje 4 es la
              condición necesaria para que sea legítima y sostenible.
            </p>

            {/* Top 3 axes — value pillars */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ViaAxisPillar
                number={1}
                label="Valor para las Personas"
                description="Mejora experiencia de pacientes, familias y colaboradores"
                question="¿Qué experiencia mejora?"
                color="cyan"
                value={axisIndicator('Personas')}
                tooltip="Valor económico anual (MM CLP) declarado por las iniciativas cuyo eje principal es Personas. Si aún no hay montos cargados, se muestra el número de iniciativas asociadas a este eje."
              />
              <ViaAxisPillar
                number={2}
                label="Valor para la Operación"
                description="Optimiza procesos clínicos y administrativos"
                question="¿Qué proceso mejora?"
                color="violet"
                value={axisIndicator('Operación')}
                tooltip="Valor económico anual (MM CLP) declarado por las iniciativas cuyo eje principal es Operación. Si aún no hay montos cargados, se muestra el número de iniciativas asociadas a este eje."
              />
              <ViaAxisPillar
                number={3}
                label="Productividad y Sostenibilidad"
                description="Aumenta capacidad con recursos responsables"
                question="¿Cuánto tiempo se ahorra?"
                color="emerald"
                value={axisIndicator('Productividad')}
                tooltip="Valor económico anual (MM CLP) declarado por las iniciativas cuyo eje principal es Productividad. Si aún no hay montos cargados, se muestra el número de iniciativas asociadas a este eje."
              />
            </div>

            {/* Divider with arrow */}
            <div className="flex items-center gap-3 px-2">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Condición obligatoria ↓
              </span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Axis 4 — foundation bar */}
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
              {/* Mobile: stacked layout / Desktop: horizontal */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-blue-500 shadow-lg shadow-blue-500/25 flex items-center justify-center shrink-0">
                    <HugeiconsIcon icon={SecurityCheckIcon} size={18} className="text-white" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-bold text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full">
                        EJE 4
                      </span>
                      <p className="text-sm font-bold text-foreground">Confianza y Uso Responsable</p>
                      <TooltipProvider delayDuration={200}>
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <button
                              className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                              aria-label="Información sobre Eje 4">
                              <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                            Índice de confianza (0–100), donde mayor es mejor. Se calcula como 100 − exposición al
                            riesgo. La exposición combina el nivel de riesgo promedio del portafolio, la Shadow AI sin
                            mitigar y las decisiones pendientes del Comité. Es la condición obligatoria: una iniciativa
                            que no cumple este eje no se aprueba.
                          </TooltipContent>
                        </UITooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                      Privacidad, ciberseguridad, transparencia, supervisión humana, cumplimiento regulatorio. Si no
                      cumple este eje, no se aprueba.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:block sm:text-right shrink-0 border-t border-blue-500/10 pt-3 sm:border-0 sm:pt-0">
                  <p className="text-[10px] text-muted-foreground sm:order-2">
                    Índice de confianza
                    <span className="block text-[9px] text-muted-foreground/70">
                      Exposición al riesgo: {t.exposure}/100
                    </span>
                  </p>
                  <p className="font-display text-2xl font-extrabold text-blue-500">{100 - t.exposure}/100</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MotionSection>

      {/* ── GOVERNANCE CYCLE ── */}
      <MotionSection delay={0.15}>
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <h2 className="font-display text-lg font-extrabold">Ciclo de gobierno</h2>
            <TooltipProvider delayDuration={200}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <button
                    className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                    aria-label="Cómo leer el ciclo de gobierno">
                    <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                  Cada etapa mide una fuente distinta: Detectar cuenta Shadow AI sin gobernar; Decidir, casos en el
                  Comité; Reevaluar, reevaluaciones activas; y el resto, iniciativas según su etapa. Por eso los números
                  no suman el total de iniciativas registradas.
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </div>
          <p className="text-xs text-muted-foreground mb-5 max-w-2xl">
            Estado del portafolio a lo largo del proceso de gobernanza. Cada paso refleja una fuente distinta, no una
            partición de las iniciativas registradas.
          </p>
          <div className="relative rounded-2xl border border-border/50 bg-card p-6 overflow-hidden">
            {/* Pipeline flow */}
            <div className="flex items-stretch gap-0">
              {CYCLE_STEPS.map((s, idx) => (
                <div key={s.key} className="flex items-stretch flex-1 min-w-0">
                  {/* Step */}
                  <Link
                    to={CYCLE_LINKS[s.key] || '/registro'}
                    aria-label={`${s.key}: ${data.cycle[s.key] ?? 0}. Ver detalle`}
                    className="flex flex-col items-center text-center flex-1 relative z-10 group rounded-xl px-1 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                    {/* Number badge */}
                    <span className="text-[10px] font-bold text-muted-foreground/60 mb-2">0{idx + 1}</span>
                    {/* Icon circle */}
                    <div
                      className={cn(
                        'h-11 w-11 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110',
                        idx === 0
                          ? 'bg-rose-500 shadow-rose-500/25'
                          : idx === CYCLE_STEPS.length - 1
                            ? 'bg-emerald-500 shadow-emerald-500/25'
                            : 'bg-primary shadow-primary/25',
                      )}>
                      <HugeiconsIcon icon={s.icon} size={18} className="text-white" aria-hidden="true" />
                    </div>
                    {/* Label */}
                    <p className="mt-2.5 text-xs font-bold text-foreground group-hover:text-primary-glow transition-colors">
                      {s.key}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 px-1">{s.desc}</p>
                    {/* Value */}
                    <p className="mt-2 font-display text-xl font-extrabold tabular text-foreground">
                      {data.cycle[s.key] ?? 0}
                    </p>
                  </Link>
                  {/* Arrow connector */}
                  {idx < CYCLE_STEPS.length - 1 && (
                    <div className="flex items-center justify-center w-6 shrink-0 self-center -mt-4">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                        className="text-muted-foreground/40">
                        <path
                          d="M4 10h10M11 6l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Subtle progress line behind */}
            <div className="absolute top-1/2 left-12 right-12 h-px bg-border/60 -translate-y-2" aria-hidden="true" />
          </div>
        </div>
      </MotionSection>

      {/* ── CHARTS ── */}
      <MotionSection delay={0.2}>
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-[0_20px_60px_-15px_rgba(103,30,117,0.12)]">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-sm font-bold">Distribución por riesgo residual</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={riskData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    strokeWidth={0}>
                    {riskData.map((d) => (
                      <Cell key={d.name} fill={RISK_HSL[d.name] || 'hsl(220 25% 55%)'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(v) => <span className="text-xs text-foreground">{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-[0_20px_60px_-15px_rgba(103,30,117,0.12)]">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-sm font-bold">Distribución por proveedor</CardTitle>
              <TooltipProvider delayDuration={200}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                      aria-label="Información sobre distribución por proveedor">
                      <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                    Cantidad de iniciativas agrupadas por proveedor tecnológico principal. Ayuda a identificar
                    concentración de riesgo y dependencia de un solo vendor.
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={providerData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={130}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'hsl(var(--muted) / 0.3)' }} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-[0_20px_60px_-15px_rgba(103,30,117,0.12)]">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-sm font-bold">Evolución del portafolio</CardTitle>
              <TooltipProvider delayDuration={200}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                      aria-label="Información sobre evolución del portafolio">
                      <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                    Tendencia mensual del portafolio. Muestra el crecimiento de iniciativas registradas vs. las que
                    llegan a producción. Permite evaluar la velocidad de maduración del ecosistema IA.
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.trend} margin={{ left: -20, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="gIni" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey="iniciativas"
                    name="Iniciativas"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#gIni)"
                  />
                  <Area
                    type="monotone"
                    dataKey="produccion"
                    name="En producción"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    fill="transparent"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </MotionSection>

      {/* ── ADOPTION & VALUE — cómo va la adopción de IA ── */}
      <MotionSection delay={0.22}>
        <div>
          <div className="flex items-center gap-2 mb-5">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <HugeiconsIcon icon={Rocket01Icon} size={14} className="text-primary-glow" aria-hidden="true" />
            </div>
            <h2 className="font-display text-lg font-extrabold">Adopción y valor</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ValueGeneratedCard valueAxes={data.value_axes} />
            <GovernanceCoverageCard governance={data.governance} />
            <AdoptionFunnelCard funnel={data.funnel} />
          </div>
        </div>
      </MotionSection>

      {/* ── PORTFOLIO SCATTER + LISTS ── */}
      <MotionSection delay={0.25}>
        <div className="grid lg:grid-cols-5 gap-4">
          <Card
            className="border-border/50 rounded-2xl lg:col-span-3 transition-all duration-300 hover:shadow-float"
            data-testid="portfolio-scatter">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <HugeiconsIcon icon={SparklesIcon} size={14} className="text-primary-glow" aria-hidden="true" />
                </div>
                <CardTitle className="font-display text-sm font-bold">Portafolio · valor esperado vs riesgo</CardTitle>
                <TooltipProvider delayDuration={200}>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                        aria-label="Información sobre gráfico de portafolio">
                        <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                      Mapa de burbujas que cruza riesgo residual (eje X) con valor económico estimado en MM CLP (eje Y).
                      El tamaño representa la cantidad de usuarios impactados. Ideal para priorizar: buscar valor alto
                      con riesgo controlado.
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Tamaño de burbuja = usuarios estimados · MM CLP de valor anual estimado
              </p>
            </CardHeader>
            <CardContent className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ left: 10, right: 20, top: 16, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    type="number"
                    dataKey="risk_score"
                    name="Riesgo"
                    domain={[0, 110]}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: 'Riesgo residual →',
                      position: 'insideBottom',
                      offset: -8,
                      fontSize: 11,
                      fill: 'hsl(var(--muted-foreground))',
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="value"
                    name="Valor"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: 'Valor (MM CLP) →',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 4,
                      fontSize: 11,
                      fill: 'hsl(var(--muted-foreground))',
                    }}
                  />
                  <ZAxis type="number" dataKey="users" range={[80, 700]} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ payload }) => {
                      const p = payload?.[0]?.payload;
                      if (!p) return null;
                      return (
                        <div className="bg-card border border-border rounded-xl shadow-float p-3 text-xs">
                          <p className="font-bold">{p.name}</p>
                          <p className="text-muted-foreground mt-1">
                            {p.department} · {p.stage}
                          </p>
                          <p className="mt-1">
                            Riesgo: <span className="font-medium">{p.risk_level}</span> · Valor: {p.value} MM ·{' '}
                            {p.users} usuarios
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={data.portfolio}>
                    {data.portfolio.map((p) => (
                      <Cell key={p.id} fill={RISK_HSL[p.risk_level]} fillOpacity={0.75} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-float">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <HugeiconsIcon icon={AlertCircleIcon} size={14} className="text-amber-500" aria-hidden="true" />
                  </div>
                  <CardTitle className="font-display text-sm font-bold">Alertas y vencimientos</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {(!data.alerts || data.alerts.length === 0) && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center mb-2">
                      <HugeiconsIcon
                        icon={SecurityCheckIcon}
                        size={18}
                        className="text-emerald-500"
                        aria-hidden="true"
                      />
                    </div>
                    <p className="text-sm font-medium text-foreground">Sin alertas ni vencimientos</p>
                    <p className="text-[11px] text-muted-foreground mt-1 max-w-[220px] leading-relaxed">
                      No hay revisiones próximas ni tareas vencidas. El portafolio está al día.
                    </p>
                  </div>
                )}
                {data.alerts.slice(0, 5).map((a, i) => (
                  <Link
                    to={a.link}
                    key={i}
                    className="flex items-start gap-3 rounded-2xl border border-border/50 p-3 transition-all duration-300 hover:shadow-card hover:border-primary/25 group"
                    data-testid={`alert-${i}`}>
                    <div
                      className={cn(
                        'h-8 w-8 rounded-full shadow-lg flex items-center justify-center shrink-0 mt-0.5',
                        a.type === 'shadow'
                          ? 'bg-rose-500 shadow-rose-500/25'
                          : a.type === 'task'
                            ? 'bg-amber-500 shadow-amber-500/25'
                            : 'bg-primary shadow-primary/25',
                      )}>
                      <HugeiconsIcon
                        icon={a.type === 'shadow' ? Radar01Icon : a.type === 'task' ? AlertCircleIcon : Calendar03Icon}
                        size={14}
                        className="text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium leading-snug group-hover:text-primary-glow transition-colors">
                        {a.text}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(a.date)}</p>
                    </div>
                    <HugeiconsIcon
                      icon={ArrowRight01Icon}
                      size={14}
                      className="text-muted-foreground group-hover:text-primary-glow group-hover:translate-x-0.5 transition-all shrink-0 mt-1"
                      aria-hidden="true"
                    />
                  </Link>
                ))}
              </CardContent>
            </Card>

            <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-float">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <HugeiconsIcon
                      icon={ChartHistogramIcon}
                      size={14}
                      className="text-primary-glow"
                      aria-hidden="true"
                    />
                  </div>
                  <CardTitle className="font-display text-sm font-bold">Mayor valor · mayor riesgo</CardTitle>
                  <TooltipProvider delayDuration={200}>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                          aria-label="Información sobre mayor valor y mayor riesgo">
                          <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                        Top de iniciativas ordenadas por valor económico esperado (arriba) y por nivel de riesgo
                        residual (abajo). Permite identificar rápidamente qué proteger y qué mitigar.
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.top_value.slice(0, 3).map((i) => (
                  <Link
                    key={i.id}
                    to={`/iniciativas/${i.id}`}
                    className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{i.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{i.roi}</p>
                    </div>
                    <RiskBadge level={i.value} />
                  </Link>
                ))}
                <div className="border-t border-border my-2" />
                {data.top_risk.slice(0, 3).map((i) => (
                  <Link
                    key={i.id}
                    to={`/iniciativas/${i.id}`}
                    className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{i.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{i.department}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <RiskBadge level={i.risk} />
                      <StageBadge stage={i.stage} />
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </MotionSection>
    </div>
  );
}

function StatCard({ icon, label, value, subtitle, variant = 'default', tooltip, to }) {
  const iconCircle =
    variant === 'alert' ? 'bg-amber-500 shadow-lg shadow-amber-500/25' : 'bg-primary shadow-lg shadow-primary/25';

  const Wrapper = to ? Link : 'div';
  const wrapperProps = to
    ? { to, 'aria-label': `${label}: ${value}. Ver detalle` }
    : { role: 'group', 'aria-label': `${label}: ${value}` };

  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        'block rounded-2xl border border-border/50 bg-card p-5 transition-all duration-300 hover:shadow-float hover:-translate-y-0.5 hover:border-primary/30',
        to && 'group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      )}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-1.5 max-w-[70%]">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">
            {label}
          </span>
          {tooltip && (
            <TooltipProvider delayDuration={200}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <button
                    className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                    aria-label={`Información sobre ${label}`}>
                    <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                  {tooltip}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
        </div>
        <div className={cn('h-8 w-8 rounded-full flex items-center justify-center shrink-0', iconCircle)}>
          <HugeiconsIcon icon={icon} size={14} className="text-white" aria-hidden="true" />
        </div>
      </div>
      <p className="font-display text-3xl font-extrabold tabular text-foreground mt-3">{value ?? '—'}</p>
      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
        {subtitle}
        {to && (
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            size={12}
            className="text-muted-foreground/50 group-hover:text-primary-glow group-hover:translate-x-0.5 transition-all"
            aria-hidden="true"
          />
        )}
      </p>
    </Wrapper>
  );
}

function ExposureCard({ value }) {
  const percentage = Math.min(value, 100);
  const color = percentage > 70 ? 'bg-rose-500' : percentage > 40 ? 'bg-primary' : 'bg-emerald-500';
  const exposureTooltip =
    'Índice compuesto (0–100) que combina la criticidad de las iniciativas sin gobernanza formal, el nivel de riesgo promedio del portafolio y el porcentaje de Shadow AI no mitigada.';

  return (
    <div
      className="rounded-2xl border border-border/50 bg-card p-5 transition-all duration-300 hover:shadow-float hover:-translate-y-0.5 hover:border-primary/30"
      role="group"
      aria-label={`Exposición al riesgo: ${value} de 100`}>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Exposición al riesgo
        </span>
        <TooltipProvider delayDuration={200}>
          <UITooltip>
            <TooltipTrigger asChild>
              <button
                className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                aria-label="Información sobre exposición al riesgo">
                <HugeiconsIcon icon={AlertCircleIcon} size={13} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
              {exposureTooltip}
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      <p className="font-display text-3xl font-extrabold tabular text-foreground mt-3">
        {value}
        <span className="text-lg text-muted-foreground font-medium">/100</span>
      </p>
      <div className="mt-3 h-2 rounded-full bg-muted/60 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-700', color)}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

const PILLAR_COLORS = {
  cyan: {
    iconBg: 'bg-cyan-500 shadow-cyan-500/25',
    badge: 'text-cyan-600 bg-cyan-500/10',
    border: 'border-cyan-500/20',
    accent: 'bg-cyan-500',
  },
  violet: {
    iconBg: 'bg-violet-500 shadow-violet-500/25',
    badge: 'text-violet-600 bg-violet-500/10',
    border: 'border-violet-500/20',
    accent: 'bg-violet-500',
  },
  emerald: {
    iconBg: 'bg-emerald-500 shadow-emerald-500/25',
    badge: 'text-emerald-600 bg-emerald-500/10',
    border: 'border-emerald-500/20',
    accent: 'bg-emerald-500',
  },
};

const PILLAR_ICONS = {
  cyan: UserMultipleIcon,
  violet: Settings02Icon,
  emerald: ChartHistogramIcon,
};

function ViaAxisPillar({ number, label, description, question, color, value, tooltip }) {
  const colors = PILLAR_COLORS[color];
  const icon = PILLAR_ICONS[color];

  return (
    <div
      className={cn(
        'rounded-xl border p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card',
        colors.border,
        'bg-card',
      )}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('h-9 w-9 rounded-full shadow-lg flex items-center justify-center shrink-0', colors.iconBg)}>
          <HugeiconsIcon icon={icon} size={16} className="text-white" aria-hidden="true" />
        </div>
        <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full', colors.badge)}>EJE {number}</span>
      </div>
      <p className="text-sm font-bold text-foreground leading-snug">{label}</p>
      <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{description}</p>
      <p className="text-[10px] italic text-muted-foreground/70 mt-2">{question}</p>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
        <div className={cn('h-1.5 w-1.5 rounded-full', colors.accent)} aria-hidden="true" />
        <span className="text-xs text-muted-foreground">Indicador:</span>
        <span className="text-sm font-bold text-foreground">{value}</span>
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  className="text-muted-foreground/60 hover:text-muted-foreground transition-colors ml-auto"
                  aria-label={`Cómo se calcula ${label}`}>
                  <HugeiconsIcon icon={AlertCircleIcon} size={12} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                {tooltip}
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

function PortfolioHealthCard({ health }) {
  const verde = health?.verde ?? 0;
  const amarillo = health?.amarillo ?? 0;
  const rojo = health?.rojo ?? 0;
  const total = verde + amarillo + rojo || 1;

  return (
    <div
      className="rounded-2xl border border-border/50 bg-card p-5 transition-all duration-300 hover:shadow-float hover:-translate-y-0.5 hover:border-primary/30"
      role="group"
      aria-label="Salud del portafolio">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Salud del portafolio
        </span>
        <TooltipProvider delayDuration={200}>
          <UITooltip>
            <TooltipTrigger asChild>
              <button
                className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                aria-label="Información sobre salud del portafolio">
                <HugeiconsIcon icon={AlertCircleIcon} size={13} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
              Semáforo del portafolio. Verde: iniciativas en etapa saludable (producción, piloto exitoso). Amarillo:
              requieren atención (evaluación pendiente, retraso menor). Rojo: riesgo crítico o bloqueo (vencidas, sin
              evaluación, Shadow AI).
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      <div className="flex items-end gap-3 mt-3">
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
            <span className="text-xs text-muted-foreground">Verde</span>
            <span className="text-xs font-bold ml-auto">{Math.round((verde / total) * 100)}%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
            <span className="text-xs text-muted-foreground">Amarillo</span>
            <span className="text-xs font-bold ml-auto">{Math.round((amarillo / total) * 100)}%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-rose-500" />
            <span className="text-xs text-muted-foreground">Rojo</span>
            <span className="text-xs font-bold ml-auto">{Math.round((rojo / total) * 100)}%</span>
          </div>
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-muted/60 overflow-hidden flex">
        <div className="h-full bg-emerald-500" style={{ width: `${(verde / total) * 100}%` }} />
        <div className="h-full bg-amber-500" style={{ width: `${(amarillo / total) * 100}%` }} />
        <div className="h-full bg-rose-500" style={{ width: `${(rojo / total) * 100}%` }} />
      </div>
    </div>
  );
}

// ── Tarjeta: Valor generado por el portafolio ──────────────────────────────
// Muestra el valor económico anual estimado, la inversión y el ROI del
// portafolio. Todo proviene de las columnas de valor de las iniciativas.
function ValueGeneratedCard({ valueAxes }) {
  const totalAnnual = valueAxes?.total_annual_clp ?? 0;
  const totalInvestment = valueAxes?.total_investment_clp ?? 0;
  const roi = valueAxes?.roi;
  const hasData = totalAnnual > 0 || totalInvestment > 0;

  return (
    <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-float">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <HugeiconsIcon icon={ChartHistogramIcon} size={14} className="text-emerald-500" aria-hidden="true" />
          </div>
          <CardTitle className="font-display text-sm font-bold">Valor generado (anual)</CardTitle>
          <TooltipProvider delayDuration={200}>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  className="text-muted-foreground/60 hover:text-muted-foreground transition-colors ml-auto"
                  aria-label="Información sobre valor generado">
                  <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                Suma del valor económico anual estimado por las iniciativas (MM CLP), la inversión comprometida y el ROI
                resultante. Se completa a medida que cada iniciativa carga su caso de valor.
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Valor anual estimado</p>
              <p className="font-display text-2xl font-extrabold text-emerald-600">{fmtMMCLP(totalAnnual)} CLP</p>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Inversión</span>
              <span className="font-semibold">{fmtMMCLP(totalInvestment)} CLP</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">ROI portafolio</span>
              <span className="font-semibold">{roi == null ? '—' : `${Math.round(roi * 100)}%`}</span>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground">Sin caso de valor cargado aún</p>
            <p className="text-[11px] text-muted-foreground/70 mt-1 leading-relaxed">
              Registra el valor esperado en cada iniciativa para ver el retorno del portafolio.
            </p>
            <Link
              to="/valor"
              className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary hover:text-primary-glow transition-colors">
              Ir a Oficina de Valor
              <HugeiconsIcon icon={ArrowRight01Icon} size={13} aria-hidden="true" />
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Tarjeta: Cobertura de gobernanza ───────────────────────────────────────
// Mide qué tan formal y seguro es el proceso de adopción: % de iniciativas con
// evaluación de riesgo completada y % de Shadow AI ya formalizada.
function GovernanceCoverageCard({ governance }) {
  const evalPct = governance?.evaluated_pct ?? 0;
  const shadowPct = governance?.shadow_governed_pct ?? 0;

  const Bar = ({ label, pct, detail, color }) => (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-bold">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted/60 overflow-hidden">
        <div className={cn('h-full rounded-full transition-all duration-700', color)} style={{ width: `${pct}%` }} />
      </div>
      {detail && <p className="text-[10px] text-muted-foreground/70 mt-1">{detail}</p>}
    </div>
  );

  return (
    <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-float">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <HugeiconsIcon icon={Shield01Icon} size={14} className="text-blue-500" aria-hidden="true" />
          </div>
          <CardTitle className="font-display text-sm font-bold">Cobertura de gobernanza</CardTitle>
          <TooltipProvider delayDuration={200}>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  className="text-muted-foreground/60 hover:text-muted-foreground transition-colors ml-auto"
                  aria-label="Información sobre cobertura de gobernanza">
                  <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                Mide qué tan seguro es el proceso de adopción: porcentaje de iniciativas con evaluación de riesgo
                completada y porcentaje de Shadow AI ya formalizada (registrada, autorizada o convertida en iniciativa).
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-1">
        <Bar
          label="Iniciativas evaluadas"
          pct={evalPct}
          color="bg-blue-500"
          detail={`${governance?.initiatives_evaluated ?? 0} de ${governance?.initiatives_total ?? 0} con evaluación completada`}
        />
        <Bar
          label="Shadow AI bajo control"
          pct={shadowPct}
          color="bg-primary"
          detail={`${governance?.shadow_governed ?? 0} de ${governance?.shadow_total ?? 0} formalizadas`}
        />
      </CardContent>
    </Card>
  );
}

// ── Tarjeta: Embudo de adopción ────────────────────────────────────────────
// Demanda → registradas → evaluadas → en producción. Muestra dónde se estanca
// el flujo de valor.
function AdoptionFunnelCard({ funnel }) {
  const steps = [
    { key: 'demanda', label: 'Demanda', color: 'bg-slate-400' },
    { key: 'registradas', label: 'Registradas', color: 'bg-primary/70' },
    { key: 'evaluadas', label: 'Evaluadas', color: 'bg-blue-500' },
    { key: 'produccion', label: 'En producción', color: 'bg-emerald-500' },
  ];
  const max = Math.max(1, ...steps.map((s) => funnel?.[s.key] ?? 0));

  return (
    <Card className="border-border/50 rounded-2xl transition-all duration-300 hover:shadow-float">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <HugeiconsIcon icon={ChartHistogramIcon} size={14} className="text-primary-glow" aria-hidden="true" />
          </div>
          <CardTitle className="font-display text-sm font-bold">Embudo de adopción</CardTitle>
          <TooltipProvider delayDuration={200}>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  className="text-muted-foreground/60 hover:text-muted-foreground transition-colors ml-auto"
                  aria-label="Información sobre embudo de adopción">
                  <HugeiconsIcon icon={AlertCircleIcon} size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                Flujo de conversión desde la demanda recibida hasta las iniciativas en producción. Permite ver dónde se
                concentra o se estanca el portafolio.
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5 pt-1">
        {steps.map((s) => {
          const val = funnel?.[s.key] ?? 0;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-24 shrink-0">{s.label}</span>
              <div className="flex-1 h-5 rounded-md bg-muted/40 overflow-hidden">
                <div
                  className={cn('h-full rounded-md transition-all duration-700', s.color)}
                  style={{ width: `${(val / max) * 100}%` }}
                />
              </div>
              <span className="text-xs font-bold tabular w-6 text-right">{val}</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
